import React, { useEffect, useState } from 'react';
import { http, authCfg } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { Gift, Loader2, RefreshCw, CheckCircle2, Clock, PlayCircle } from 'lucide-react';

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-AU') : '—');
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const bdayLabel = (b) => {
  const [m, d] = (b || '').split('-').map(Number);
  return m ? `${d} ${MONTHS[m - 1]}` : b;
};

export default function BirthdayPanel() {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);

  const load = () => http.get('/admin/birthdays', authCfg()).then((r) => setData(r.data)).catch(() => setData({ stats: {}, upcoming: [], records: [] }));
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data: res } = await http.post('/admin/birthdays/run', {}, authCfg());
      toast({ title: res.sentCount === 0 ? 'No birthdays today (or already treated this year)' : `Sent ${res.sentCount} birthday voucher${res.sentCount === 1 ? '' : 's'} 🎂` });
      load();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Could not run check', variant: 'destructive' }); }
    finally { setRunning(false); }
  };

  if (!data) return <div className="py-16 text-center"><Loader2 className="animate-spin inline text-alp-gold" size={20} /></div>;
  const st = data.stats || {};

  return (
    <div data-testid="birthday-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h2 className="font-serif text-2xl text-alp-cream flex items-center gap-2"><Gift size={20} className="text-alp-gold" /> Birthday Rewards</h2>
        <div className="flex gap-2">
          <button onClick={runNow} disabled={running} data-testid="birthday-run-now"
            className="inline-flex items-center gap-1.5 bg-alp-gold text-alp-black px-4 py-1.5 rounded text-xs tracking-[0.12em] uppercase font-semibold hover:bg-alp-goldlight disabled:opacity-50">
            {running ? <Loader2 size={13} className="animate-spin" /> : <PlayCircle size={13} />} Run check now
          </button>
          <button onClick={load} className="inline-flex items-center gap-1.5 text-xs tracking-[0.12em] uppercase border border-alp-line px-3 py-1.5 rounded text-alp-cream hover:border-alp-gold hover:text-alp-gold"><RefreshCw size={13} /> Refresh</button>
        </div>
      </div>
      <p className="text-alp-gray text-sm mb-6">Fully automatic: every member with a birthday on file gets a personal single-use <span className="text-alp-gold">15% voucher</span> (valid 14 days, locked to their email) on their day. The system checks every 30 minutes — "Run check now" just forces it instantly.</p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="birthday-stats">
        {[['Birthdays on file', st.onFile || 0], ['Sent this year', st.sentThisYear || 0], ['Vouchers used', st.used || 0], ['Birthday revenue', money(st.revenue)]].map(([l, v]) => (
          <div key={l} className="border border-alp-line rounded-lg bg-alp-card/40 p-4">
            <p className="text-[10px] tracking-[0.2em] uppercase text-alp-gray">{l}</p>
            <p className="text-alp-gold font-serif text-xl mt-1">{v}</p>
          </div>
        ))}
      </div>

      <div className="border border-alp-line rounded-lg bg-alp-card/40 p-5 mb-8">
        <p className="text-[10px] tracking-[0.25em] uppercase text-alp-gold mb-3">Upcoming (next 60 days)</p>
        {data.upcoming.length === 0 ? <p className="text-alp-gray text-sm py-4 text-center">No upcoming birthdays — members add theirs on the Account page, or set one in the CRM.</p> : (
          <div className="border border-alp-line rounded divide-y divide-alp-line/50 max-h-72 overflow-y-auto" data-testid="birthday-upcoming">
            {data.upcoming.map((u) => (
              <div key={u.email} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
                <span className="min-w-0 flex-1">
                  <span className="block text-alp-cream">{u.name || u.email}</span>
                  <span className="block text-alp-gray text-xs">{u.email}</span>
                </span>
                <span className="text-alp-gold text-xs">🎂 {bdayLabel(u.birthday)}</span>
                <span className={`text-xs ${u.daysUntil === 0 ? 'text-emerald-300' : 'text-alp-gray'}`}>{u.daysUntil === 0 ? 'Today!' : `in ${u.daysUntil} day${u.daysUntil === 1 ? '' : 's'}`}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border border-alp-line rounded-lg bg-alp-card/40 p-5">
        <p className="text-[10px] tracking-[0.25em] uppercase text-alp-gold mb-3">Vouchers sent</p>
        {data.records.length === 0 ? <p className="text-alp-gray text-sm py-4 text-center">No birthday vouchers sent yet.</p> : (
          <div className="border border-alp-line rounded divide-y divide-alp-line/50 max-h-96 overflow-y-auto" data-testid="birthday-history">
            {data.records.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
                <span className="min-w-0 flex-1">
                  <span className="block text-alp-cream">{r.name || r.email}</span>
                  <span className="block text-alp-gray text-xs">{r.email} · code <span className="text-alp-gold">{r.code}</span> · sent {fmtDate(r.sentAt)} · expires {r.expiry}</span>
                </span>
                {r.used ? (
                  <span className="inline-flex items-center gap-1.5 text-emerald-300 text-xs" data-testid={`birthday-used-${r.email}`}>
                    <CheckCircle2 size={14} /> Used · {money(r.orderTotal)}
                  </span>
                ) : (
                  <span className="text-alp-gray text-xs inline-flex items-center gap-1.5"><Clock size={13} /> Not used yet{r.emailStatus !== 'sent' ? ` · email ${r.emailStatus}` : ''}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
